import { useCallback, useEffect, useRef, useState } from 'react';

/**
 *
 * Presenter hook for the ActivityChip action menu. Owns all open/close and
 * positioning logic so the ActionMenu component stays a dumb renderer.
 *
 * The menu is rendered through a portal to `document.body` (to escape the
 * `overflow: hidden` ancestors in the CrewTimeline rows). The shared
 * AnchoredPortalLayer owns viewport positioning and outside-click handling.
 *
 * Responsibilities:
 * - Tracks `isOpen` state.
 * - Exposes `triggerRef` as the anchor for the portaled menu.
 * - `toggleMenu` opens/closes the menu.
 * - `handleActionClick` runs a menu item's action then closes the menu.
 * - Notifies the consumer via `onOpenChange` whenever `isOpen` flips, and once
 *   more with `false` on unmount if the menu was still open.
 *
 * @param {{ onOpenChange?: (isOpen: boolean) => void }} [options]
 * @returns {{
 *   isOpen: boolean,
 *   triggerRef: React.RefObject<HTMLElement>,
 *   toggleMenu: (e: Event) => void,
 *   handleActionClick: (action: () => void) => void,
 *   closeMenu: () => void,
 * }}
 */
// ! TODO: This portal + manual viewport positioning is a patch for the missing honeycomb support.
// ! Once we upgrade to honeycomb 16+, replace this hook's positioning logic with honeycomb's `useSmartPosition`.
export const useActionMenu = ({ onOpenChange } = {}) => {
	const [isOpen, setIsOpen] = useState(false);
	const triggerRef = useRef(null);
	const onOpenChangeRef = useRef(onOpenChange);

	useEffect(() => {
		onOpenChangeRef.current = onOpenChange;
	}, [onOpenChange]);

	useEffect(() => {
		onOpenChangeRef.current?.(isOpen);

		return () => {
			if (isOpen) onOpenChangeRef.current?.(false);
		};
	}, [isOpen]);

	const closeMenu = useCallback(() => setIsOpen(false), []);

	const toggleMenu = useCallback((e) => {
		e.stopPropagation();
		setIsOpen((prev) => !prev);
	}, []);

	const handleActionClick = useCallback(
		(action) => {
			action();
			closeMenu();
		},
		[closeMenu]
	);

	return { isOpen, triggerRef, toggleMenu, handleActionClick, closeMenu };
};
